import { ScreenSizeRender, Tag } from "@jsq/ds/core";
import React, { useEffect } from "react";
import { useGetPortalAppContextQuery, useGetSessionStatusQuery } from "common/types/mainHooks";
import PreviewBanner from "apps/common/PreviewBanner";
import { useAppContext } from "apps/portal/Portal/common/AppContext";
import { useLocation, useParams } from "react-router-dom";
import { type PortalRouteParams } from "apps/portal/Portal/SingleManager/types";
import styles from "apps/portal/Portal/styles.scss";
import { useUpscope } from "@upscopeio/react";
import { useGetCoBrowseDevFeatureFlagsQuery } from "common/types/federation/supergraphHooks";

const Banner = () => {
  const { data: sessionData, refetch } = useGetSessionStatusQuery();
  const { arenaDomain } = useParams<PortalRouteParams>();
  const { user } = useAppContext();
  const location = useLocation();

  React.useEffect(() => {
    if (user) {
      refetch().then(({ data }) => {
        const currentSpoofedUser = data?.session?.userId;
        const previousSpoofedUser = sessionData?.session?.userId;
        if (previousSpoofedUser && currentSpoofedUser !== previousSpoofedUser) {
          // Force reload to reset apollo cache
          window.location.reload();
        }
      });
    }
  }, [location, refetch, sessionData, user]);

  const { Upscope } = useUpscope();
  const { data: enableCoBrowseFeature } = useGetCoBrowseDevFeatureFlagsQuery();
  const isCoBrowseFeatureEnabled =
    enableCoBrowseFeature?.devFeatures.enableCoBrowseFeature || false;

  useEffect(() => {
    if (isCoBrowseFeatureEnabled && Upscope) {
      Upscope?.("updateConnection", {
        apiKey: "SqzNmzzieU",
        uniqueId: sessionData?.session?.originalUserId,
      });
    }
  }, [Upscope]);

  const { data: portalAppData } = useGetPortalAppContextQuery();
  const portalArena = portalAppData?.managerArenas?.find(
    (a) => a.secondLevelDomain.toLowerCase() === arenaDomain?.toLowerCase(),
  );

  const isPreviewSpoof = portalArena?.hasUnifiedPortalPreview ?? false;

  const fullName = user?.fullName;
  const terminateSpoofSession = () => {
    window.location.href = "/unspoof";
  };

  const isSpoofSession = sessionData?.session?.spoofed;

  if (!isSpoofSession) {
    return null;
  }
  return (
    <PreviewBanner
      onExit={terminateSpoofSession}
      exitButtonDataCy="return-to-account-btn"
      exitButtonText="Return to your account"
    >
      {!isPreviewSpoof ? (
        <span className={styles.PreviewPortalBannerText} data-cy="portal-client-banner">
          You are signed in as portal client {fullName}
        </span>
      ) : (
        <>
          <ScreenSizeRender large>
            <Tag variant="informative">New</Tag>
          </ScreenSizeRender>
          <span className={styles.PreviewPortalBannerText}>
            You are previewing the portal experience as client {fullName}
          </span>
        </>
      )}
    </PreviewBanner>
  );
};

export default Banner;
